import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getKycRequests } from '../../../../api/kyc';
import { Badge } from '../../../../components/ui/badge';
import { useGetCustomerProfile } from '../../../../api/auth/use-customer-auth';
import SectionErrorBoundary from '../../../../components/SectionErrorBoundary';

const KycStatus = () => {
    const { t } = useTranslation('profile');
    const {
        data: kycRequests,
        isLoading: kycLoading,
        error: kycError,
    } = useQuery({
        queryKey: ['kycStatus'],
        queryFn: getKycRequests,
    });

    const { data: customerProfile, isLoading: profileLoading } = useGetCustomerProfile();

    if (kycLoading || profileLoading) return <p>{t('loadingKycStatus')}</p>;

    // If KYC requests failed to load, show customer profile KYC status as fallback
    if (kycError) {
        console.error('KYC Status Error:', kycError);

        // Show customer profile KYC status if available
        if (customerProfile?.data?.kyc_status) {
            return (
                <div className="space-y-2">
                    <h3 className="text-lg font-semibold mb-2">{t('kycStatus')}</h3>
                    <div className="space-y-2">
                        {getStatusBadge(customerProfile.data.kyc_status)}
                        <p className="text-sm text-muted-foreground">{t('statusFromProfile')}</p>
                    </div>
                    <p className="text-sm text-red-500">{t('kycDetailsNotLoaded')}</p>
                </div>
            );
        }

        return (
            <div className="space-y-2">
                <p className="text-red-500">{t('failedToLoadKyc')}</p>
                <p className="text-sm text-muted-foreground">
                    {t('tryRefreshingOrContactSupport')}
                </p>
            </div>
        );
    }

    const latestRequest = kycRequests?.[0];

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'approved':
                return (
                    <Badge variant="default" className="bg-green-600">
                        {t('approved')}
                    </Badge>
                );
            case 'pending':
                return (
                    <Badge variant="secondary" className="bg-yellow-600">
                        {t('pendingReview')}
                    </Badge>
                );
            case 'rejected':
                return <Badge variant="destructive">{t('rejected')}</Badge>;
            case 'none':
                return <Badge variant="outline">{t('notSubmitted')}</Badge>;
            default:
                return <Badge variant="outline">{t('unknown')}</Badge>;
        }
    };

    return (
        <SectionErrorBoundary sectionName={t('kycStatus')} showRetry={true} showDetails={true}>
            <div>
                <h3 className="text-lg font-semibold mb-2">{t('kycStatus')}</h3>
                {latestRequest ? (
                    <div className="space-y-2">
                        {getStatusBadge(latestRequest.status)}
                        {latestRequest.status === 'rejected' && (
                            <p className="text-sm text-red-500">
                                {t('reason')}: {latestRequest.rejection_reason}
                            </p>
                        )}
                    </div>
                ) : customerProfile?.data?.kyc_status ? (
                    <div className="space-y-2">
                        {getStatusBadge(customerProfile.data.kyc_status)}
                        <p className="text-sm text-muted-foreground">{t('statusFromProfile')}</p>
                    </div>
                ) : (
                    <p className="text-muted-foreground">{t('noKycSubmitted')}</p>
                )}
            </div>
        </SectionErrorBoundary>
    );
};

export default KycStatus;
